// ============================================================
// Umbrales clínicos para asesoría de lactancia
// Basados en OMS, UNICEF, AAP y guías del MSP Uruguay.
// ============================================================

// Pérdida de peso máxima post-nacimiento
export const WEIGHT_LOSS_THRESHOLDS = {
  green: 7,      // ≤7% normal
  yellow: 10,    // 7-10% observar
  // >10% preocupante · >12% alarma
  alarm: 12,
};

// Día en que debería recuperar el peso de nacimiento
export const RECOVERY_DAY_THRESHOLDS = {
  ideal: 10,     // recuperación esperada ≤ día 10
  latest: 14,    // límite superior día 14 (OMS/MSP)
};

// Ganancia diaria (post-recuperación) según edad
// Referencias: OMS Growth Standards + guías clínicas de lactancia.
export const DAILY_GAIN_G = {
  '0-3m':   { min: 20, ideal: 30 },   // 20-30 g/día · 150-210 g/sem
  '3-6m':   { min: 15, ideal: 20 },
  '6-12m':  { min: 8,  ideal: 12 },
};

// Micciones y deposiciones (a partir del 5º día)
export const OUTPUT_THRESHOLDS = {
  urine: { green: 6, yellow: 4 },        // pañales mojados/día
  stool: { green: 3, yellow: 2 },        // primeras 6 semanas
};

// Rango esperado de ganancia semanal (para banda del gráfico)
export function expectedWeeklyGainRange(ageInDays) {
  // Devuelve gramos por semana esperados (mínimo, ideal)
  if (ageInDays < 90) return { min: 150, ideal: 210 };  // 0-3m
  if (ageInDays < 180) return { min: 105, ideal: 140 }; // 3-6m
  return { min: 55, ideal: 85 };                        // 6-12m
}

// Peso esperado (banda verde) para un día X post-recuperación
// Este es el corazón del gráfico: dado peso de nacimiento y días transcurridos
// desde la recuperación, cuánto tendría que estar pesando.
export function expectedWeightRange(birthWeight, ageInDays, recoveryDay = 10) {
  // Antes de la recuperación: puede perder hasta 7-10% (normal)
  if (ageInDays <= recoveryDay) {
    // Curva típica: baja hasta ~5% en día 3-4, recupera al día 10
    const minPct = ageInDays <= 4 ? -10 : Math.min(-10 + (ageInDays - 4) * 1.7, 0);
    const idealPct = ageInDays <= 3 ? -5 : Math.min(-5 + (ageInDays - 3) * 0.7, 0);
    return {
      min: birthWeight * (1 + minPct / 100),
      ideal: birthWeight * (1 + idealPct / 100),
    };
  }
  // Post-recuperación: aplicar ganancia esperada por edad
  const daysPost = ageInDays - recoveryDay;
  const gainRange = expectedWeeklyGainRange(ageInDays);
  return {
    min: birthWeight + (gainRange.min / 7) * daysPost,
    ideal: birthWeight + (gainRange.ideal / 7) * daysPost,
  };
}

// ============================================================
// Motor de interpretación clínica
// ============================================================
export function interpret(caseData, weights) {
  const {
    birthWeight, currentWeight, minWeight, minWeightDay,
    ageInDays, gestationalAgeWeeks,
    birthType, complications, urineFreq, stoolFreq,
    generalState,
  } = caseData;

  const findings = { favorable: [], indagar: [], alarma: [] };

  // 1. Pérdida máxima
  if (birthWeight && minWeight && minWeightDay != null) {
    const lossPct = ((birthWeight - minWeight) / birthWeight) * 100;
    if (lossPct <= WEIGHT_LOSS_THRESHOLDS.green) {
      findings.favorable.push(
        `Pérdida máxima de ${lossPct.toFixed(1)}% (día ${minWeightDay}) — dentro de rango normal (≤7%).`
      );
    } else if (lossPct <= WEIGHT_LOSS_THRESHOLDS.yellow) {
      findings.indagar.push(
        `Pérdida máxima de ${lossPct.toFixed(1)}% (día ${minWeightDay}) — en zona de observación (7-10%). Evaluar frecuencia y eficacia de tomas.`
      );
    } else if (lossPct <= WEIGHT_LOSS_THRESHOLDS.alarm) {
      findings.indagar.push(
        `Pérdida máxima de ${lossPct.toFixed(1)}% (día ${minWeightDay}) — supera el 10%. Requiere intervención en lactancia y valorar interconsulta.`
      );
    } else {
      findings.alarma.push(
        `Pérdida máxima de ${lossPct.toFixed(1)}% (día ${minWeightDay}) — supera 12%. Sugerir interconsulta pediátrica urgente.`
      );
    }
  }

  // 2. Recuperación del peso de nacimiento
  const recoveryEntry = weights.find(w => w.weight >= birthWeight && w.day > (minWeightDay || 0));
  if (recoveryEntry) {
    if (recoveryEntry.day <= RECOVERY_DAY_THRESHOLDS.ideal) {
      findings.favorable.push(
        `Recuperación del peso de nacimiento al día ${recoveryEntry.day} — dentro de lo esperado.`
      );
    } else if (recoveryEntry.day <= RECOVERY_DAY_THRESHOLDS.latest) {
      findings.indagar.push(
        `Recuperación al día ${recoveryEntry.day} — dentro del límite superior (día 14). Continuar seguimiento estrecho.`
      );
    } else {
      findings.alarma.push(
        `Recuperación al día ${recoveryEntry.day} — más allá del día 14. Sugerir interconsulta pediátrica.`
      );
    }
  } else if (birthWeight && currentWeight && ageInDays > RECOVERY_DAY_THRESHOLDS.latest && currentWeight < birthWeight) {
    findings.alarma.push(
      `A los ${ageInDays} días aún no recupera el peso de nacimiento. Sugerir interconsulta pediátrica.`
    );
  }

  // 3. Ganancia media diaria post-recuperación
  if (recoveryEntry && currentWeight && ageInDays > recoveryEntry.day) {
    const gramsGained = currentWeight - recoveryEntry.weight;
    const daysSinceRecovery = ageInDays - recoveryEntry.day;
    if (daysSinceRecovery > 0) {
      const gainPerDay = gramsGained / daysSinceRecovery;
      const range = ageInDays < 90
        ? DAILY_GAIN_G['0-3m']
        : ageInDays < 180 ? DAILY_GAIN_G['3-6m'] : DAILY_GAIN_G['6-12m'];
      const label = `Ganancia media diaria post-recuperación: ${gainPerDay.toFixed(1)} g/día (${daysSinceRecovery} días).`;
      if (gainPerDay >= range.ideal) {
        findings.favorable.push(`${label} Rango esperado ≥${range.ideal} g/día. Muy buen ritmo.`);
      } else if (gainPerDay >= range.min) {
        findings.favorable.push(`${label} Dentro del rango esperado (${range.min}-${range.ideal} g/día).`);
      } else if (gainPerDay > 0) {
        findings.indagar.push(`${label} Por debajo del mínimo esperado (${range.min} g/día). Revisar frecuencia y agarre.`);
      } else {
        findings.alarma.push(`${label} Sin ganancia o pérdida. Sugerir interconsulta pediátrica.`);
      }
    }
  }

  // 4. Prematurez
  if (gestationalAgeWeeks != null && gestationalAgeWeeks < 37) {
    findings.indagar.push(
      `Nacimiento pretérmino (${gestationalAgeWeeks} semanas) — considerar edad corregida y necesidades específicas de lactancia.`
    );
  }

  // 5. Circunstancias de riesgo del parto
  if (complications?.cesarea) findings.indagar.push('Parto por cesárea — puede haber demora en el reflejo de eyección; observar apego y frecuencia inicial.');
  if (complications?.separacion) findings.indagar.push('Separación madre-bebé al nacer — puede afectar instalación de lactancia; reforzar acompañamiento.');
  if (complications?.sueros) findings.indagar.push('Sueros IV maternos durante el parto — pueden inflar peso de nacimiento aparente y alterar cálculo real de pérdida.');
  if (complications?.hipoglucemia) findings.indagar.push('Hipoglucemia neonatal registrada — evaluar si hubo suplementación y su impacto en la instalación de la lactancia.');
  if (complications?.ictericia) findings.indagar.push('Ictericia registrada — valorar hidratación y frecuencia de tomas; buena lactancia favorece resolución.');
  if (complications?.fototerapia) findings.indagar.push('Fototerapia — asegurar frecuencia de tomas y hidratación adecuada durante y post-tratamiento.');
  if (complications?.internacion) findings.indagar.push('Internación neonatal — indagar si hubo administración de fórmula, uso de biberón o chupete, y proceso de reinstalación de la lactancia.');

  // 6. Eliminaciones (a partir del 5º día)
  if (ageInDays >= 5) {
    if (urineFreq != null) {
      if (urineFreq >= OUTPUT_THRESHOLDS.urine.green) {
        findings.favorable.push(`Micciones: ${urineFreq}/día — hidratación adecuada.`);
      } else if (urineFreq >= OUTPUT_THRESHOLDS.urine.yellow) {
        findings.indagar.push(`Micciones: ${urineFreq}/día — límite bajo. Evaluar frecuencia y eficacia de tomas.`);
      } else {
        findings.alarma.push(`Micciones: ${urineFreq}/día — por debajo del mínimo (${OUTPUT_THRESHOLDS.urine.green}/día). Signo de hidratación insuficiente.`);
      }
    }
    if (stoolFreq != null) {
      if (stoolFreq >= OUTPUT_THRESHOLDS.stool.green) {
        findings.favorable.push(`Deposiciones: ${stoolFreq}/día — ingesta adecuada.`);
      } else if (stoolFreq >= OUTPUT_THRESHOLDS.stool.yellow) {
        findings.indagar.push(`Deposiciones: ${stoolFreq}/día — límite bajo para primeras 6 semanas.`);
      } else if (ageInDays < 42) {
        findings.indagar.push(`Deposiciones: ${stoolFreq}/día — por debajo del mínimo esperado en las primeras 6 semanas.`);
      }
    }
  }

  // 7. Estado general
  if (generalState === 'somnoliento' || generalState === 'cuesta_despertar') {
    findings.indagar.push('Estado somnoliento / cuesta despertar — puede indicar toma insuficiente. Reforzar despertar activo para tomas.');
  }
  if (generalState === 'irritable') {
    findings.indagar.push('Irritabilidad marcada — indagar posibles causas: hambre, agarre doloroso, reflujo, otros.');
  }

  // 8. Si no hay hallazgos favorables ni alarmas y el bebé aumenta bien, dar nota positiva general
  if (findings.favorable.length === 0 && findings.alarma.length === 0 && currentWeight && birthWeight) {
    if (currentWeight > birthWeight) {
      findings.favorable.push('Peso actual por encima del de nacimiento.');
    }
  }

  return findings;
}
